"use client";

import { useState } from "react";
import { formatDistance } from "date-fns";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  Play,
  Trash2,
  Flag,
  RotateCcw,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { API_BASE_URL } from "@/lib/config";
import useSWR from "swr";
import { toast } from "sonner";

const fetcher = (url: string) => fetch(url).then((res) => res.json());

interface RunsOverviewProps {
  sessionId: string;
  onViewRunDetails: (runId: string, runData: any) => void;
  onBackToPlan: () => void;
}

export function RunsOverview({
  sessionId,
  onViewRunDetails,
  onBackToPlan,
}: RunsOverviewProps) {
  const { data: runsData, mutate } = useSWR(
    `${API_BASE_URL}/strategies/strategy-runs-by-sessionID/${sessionId}`,
    fetcher
  );
  const [sortBy, setSortBy] = useState<"createdAt" | "duration" | "status">(
    "createdAt"
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [deletingRunId, setDeletingRunId] = useState<string | null>(null);

  if (!runsData || !runsData.runs) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <div className="text-center py-8">
          <Play className="h-12 w-12 mx-auto mb-4 opacity-50 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">No Strategy Runs Found</h3>
          <p className="text-muted-foreground mb-4">
            Execute your strategy to see run history here.
          </p>
        </div>
      </div>
    );
  }

  const formatDate = (timestamp: number) => {
    return formatDistance(new Date(timestamp * 1000), new Date(), {
      addSuffix: true,
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "error":
        return <XCircle className="h-4 w-4 text-red-600" />;
      case "running":
        return <Play className="h-4 w-4 text-blue-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "success":
        return (
          <Badge
            variant="default"
            className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
          >
            Success
          </Badge>
        );
      case "error":
        return <Badge variant="destructive">Error</Badge>;
      case "running":
        return (
          <Badge
            variant="default"
            className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
          >
            Running
          </Badge>
        );
      default:
        return <Badge variant="secondary">Draft</Badge>;
    }
  };

  // Find the latest run by createdAt timestamp
  const latestRunId =
    runsData.runs.length > 0
      ? runsData.runs.reduce((latest: any, current: any) =>
          current.createdAt > latest.createdAt ? current : latest
        ).id
      : null;

  const sortedRuns = [...runsData.runs].sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case "createdAt":
        aValue = a.createdAt;
        bValue = b.createdAt;
        break;
      case "status":
        aValue = a.status;
        bValue = b.status;
        break;
      default:
        aValue = a.createdAt;
        bValue = b.createdAt;
    }

    if (sortOrder === "asc") {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleSort = (column: "createdAt" | "status") => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
  };

  const handleDeleteRun = async (runId: string, runName: string) => {
    setDeletingRunId(runId);
    
    try {
      // Optimistic update - remove the run from the UI immediately
      const optimisticData = {
        ...runsData,
        runs: runsData.runs.filter((run: any) => run.id !== runId)
      };
      mutate(optimisticData, false);

      // Make the API call
      const response = await fetch(`${API_BASE_URL}/strategies/strategy-runs/${runId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete run: ${response.statusText}`);
      }

      // Revalidate the data to ensure consistency
      mutate();
      toast.success(`Run "${runName}" deleted successfully`);
    } catch (error) {
      // Revert optimistic update on error
      mutate();
      toast.error(`Failed to delete run "${runName}"`);
    } finally {
      setDeletingRunId(null);
    }
  };

  const successfulRuns = runsData.runs.filter(
    (run: any) => run.status === "success"
  ).length;
  const failedRuns = runsData.runs.filter(
    (run: any) => run.status === "error"
  ).length;
  const averageDuration =
    runsData.runs.reduce((sum: any, run: any) => sum + run.duration, 0) /
    runsData.runs.length;
  const successRate =
    runsData.runs.length > 0
      ? (successfulRuns / runsData.runs.length) * 100
      : 0;

  return (
    <div className="flex flex-col h-full">
      <div className="rounded-md border overflow-hidden">
        <div className="overflow-x-auto max-w-full">
          <Table className="table-fixed w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40%] pl-4 pr-2 md:px-4">Name</TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50 w-[13%] px-2 md:px-4"
                  onClick={() => handleSort("status")}
                >
                  Status{""}
                  {sortBy === "status" && (sortOrder === "asc" ? "↑" : "↓")}
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50 w-[18%] px-2 md:px-4"
                  onClick={() => handleSort("createdAt")}
                >
                  Created{" "}
                  {sortBy === "createdAt" && (sortOrder === "asc" ? "↑" : "↓")}
                </TableHead>
                <TableHead className="w-[12%] text-center px-2 md:px-4">Steps</TableHead>
                <TableHead className="w-[10%] px-2 md:px-4">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRuns.map((run) => (
                <TableRow
                  key={run.id}
                  className="cursor-pointer hover:bg-muted/50"
                >
                  <TableCell className="font-medium pl-4 pr-2 md:px-4">
                    <div className="flex items-center gap-2 overflow-x-auto md:overflow-x-visible max-w-full">
                      {run.id === latestRunId && (
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1.5 py-0.5 bg-muted text-white border-border shrink-0"
                        >
                          <Play className="h-3 w-3 mr-1" />
                          Current
                        </Badge>
                      )}
                      <div className="whitespace-nowrap md:truncate min-w-0" title={run.name}>
                        {run.name}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="px-2 md:px-4">
                    <div className="flex items-center">
                      {getStatusIcon(run.status)}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground px-2 md:px-4">
                    <div className="truncate">{formatDate(run.createdAt)}</div>
                  </TableCell>
                  <TableCell className="text-center px-2 md:px-4">{run.stepCount}</TableCell>
                  <TableCell className="px-2 md:px-4">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          onViewRunDetails(run.id, {
                            ...run,
                            isLatest: run.id === latestRunId,
                          })
                        }
                        className="h-8 w-8 p-0"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            disabled={deletingRunId === run.id}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Strategy Run</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{run.name}"? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteRun(run.id, run.name)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {runsData.runs.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Play className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No runs yet. Execute your strategy to see results here.</p>
        </div>
      )}
    </div>
  );
}
